<template>
  <Teleport to="body">
    <div v-bind="$attrs" role="dialog" aria-modal="true" class="detail-drawer">
      <div class="detail-drawer-overlay" ref="overlay" @click="close"></div>
      <aside class="detail-drawer-panel" ref="panel">
        <header class="detail-drawer-header">
          <button class="detail-drawer-back" @click="back" aria-label="Back" v-if="showBack">
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
            <span>Back</span>
          </button>
          <button class="detail-drawer-close" @click="close" aria-label="Close">
            <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </header>
        <div class="detail-drawer-body" ref="body">
          <slot></slot>
        </div>
      </aside>
    </div>
  </Teleport>
</template>

<script>
import Teleport from 'vue2-teleport';
import swipeToDismiss from './swipe-to-dismiss';

// Ref-counted scroll lock: keeps the page behind the drawer fixed while it is
// open, releasing only once the last stacked drawer/modal has closed.
// NOTE: this layout's scroll container is the viewport (<html>), not <body>
// (body uses `overflow-x: clip` so it is not a scroll container), so the lock
// must be applied to documentElement — locking body alone does nothing here.
function lockBodyScroll() {
  const root = document.documentElement;
  const body = document.body;
  const count = parseInt(root.dataset.lpScrollLock || '0', 10) + 1;
  root.dataset.lpScrollLock = String(count);

  if (count === 1) {
    const scrollbar = window.innerWidth - root.clientWidth;
    if (scrollbar > 0) {
      root.dataset.lpScrollPad = body.style.paddingRight || '';
      body.style.paddingRight = scrollbar + 'px'; // avoid layout shift when the scrollbar hides
    }
    root.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
  }
}

function unlockBodyScroll() {
  const root = document.documentElement;
  const body = document.body;
  const count = Math.max(0, parseInt(root.dataset.lpScrollLock || '1', 10) - 1);

  if (count === 0) {
    root.style.overflow = '';
    body.style.overflow = '';
    body.style.paddingRight = root.dataset.lpScrollPad || '';
    delete root.dataset.lpScrollLock;
    delete root.dataset.lpScrollPad;
  } else {
    root.dataset.lpScrollLock = String(count);
  }
}

export default {
  name: 'lp-drawer',
  inheritAttrs: false,
  mixins: [swipeToDismiss],
  props: {
    showBack: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    Teleport,
  },
  mounted() {
    lockBodyScroll();
  },
  beforeDestroy() {
    unlockBodyScroll();
  },
  methods: {
    close() {
      this.$emit('close');
    },
    back() {
      this.$emit('back');
    },
  },
};
</script>

<style lang="scss">
/* ============ DETAIL DRAWER ============ */
/* Structural shell rendered by <lp-drawer>. Styling (incl. the 24px panel
   radius) lives here so the drawer renders identically on every page that
   uses this component — the landing page and the registration take-over. */
.detail-drawer {
  position: fixed;
  inset: 0;
  z-index: 250;
  display: flex;
  justify-content: flex-end;
}
.detail-drawer[hidden] { display: none; }
.detail-drawer-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.35);
  backdrop-filter: blur(2px);
  animation: drawer-fade 0.18s ease-out;
}
@keyframes drawer-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.detail-drawer-panel {
  position: relative;
  width: 100%;
  max-width: 540px;
  height: 100%;
  background: var(--bg);
  border-radius: 24px;
  box-shadow: -16px 0 40px rgba(0,0,0,0.18);
  display: flex;
  flex-direction: column;
  animation: drawer-slide 0.24s cubic-bezier(0.2, 0.7, 0.2, 1);
}
@keyframes drawer-slide {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}
.detail-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 0.5px solid var(--border);
  flex-shrink: 0;
}
.detail-drawer-back {
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  font-family: inherit;
  font-size: var(--fs-small);
  font-weight: 500;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 6px 10px 6px 6px;
  border-radius: var(--radius-sm);
  transition: background 0.15s, color 0.15s;
}
.detail-drawer-back:hover { background: var(--surface); color: var(--text); }
.detail-drawer-close {
  background: transparent;
  border: 0.5px solid var(--border);
  color: var(--text-secondary);
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.detail-drawer-close:hover { background: var(--surface); border-color: var(--border-strong); color: var(--text); }
.detail-drawer-body {
  flex: 1;
  // See the note in lp-modal: without this the scroller refuses to shrink
  // inside the sheet on mobile Safari and the content stops scrolling.
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain; // don't hand the scroll to the page behind
  -webkit-overflow-scrolling: touch;
  padding: 32px;
}

@media (max-width: 800px) {
  .detail-drawer-panel { max-width: 100%; }
  .detail-drawer-body { padding: 22px 20px; }
}

/* ---- Mobile: drawer → bottom sheet ---- */
@keyframes sheetUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
@media (max-width: 640px) {
  .detail-drawer {
    justify-content: stretch;
    align-items: flex-end;
  }
  .detail-drawer-overlay { background: rgba(0, 0, 0, 0.45); }
  .detail-drawer-panel {
    width: 100%;
    max-width: 100%;
    height: 93%;
    border-radius: 24px 24px 0 0;
    box-shadow: 0 -12px 40px rgba(0, 0, 0, 0.26);
    animation: sheetUp 0.28s cubic-bezier(0.2, 0.7, 0.2, 1);

    &::before {
      content: "";
      flex-shrink: 0;
      width: 38px;
      height: 5px;
      border-radius: 3px;
      background: var(--border);
      margin: 10px auto 2px;
    }
  }
  .detail-drawer-header { padding: 4px 16px 12px; }
  .detail-drawer-body { padding: 24px 22px 32px; }
}
</style>
